import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm, useWatch, type Control } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { isIsoDay } from '@/lib/datetime';
import { splitHours, toDecimalHours } from '@/lib/hours';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { STANDARD_MONTHLY_HOURS } from '../constants';
import { useNextStaffInvoiceNumber, useStaffInvoiceSalary } from '../queries';
import { resolveStaffInvoiceEmployeeLabel, searchStaffInvoiceEmployees } from '../select';
import { StaffInvoiceLoggedHours } from './StaffInvoiceLoggedHours';
import type { StaffInvoiceSalary } from '../api';
import type { StaffInvoiceInput } from '../types';

const schema = z
  .object({
    employee: z.string().min(1, 'Pick an employee'),
    number: z.string().max(50),
    service_description: z.string().max(255),
    period_start: z.string().min(1, 'Required'),
    period_end: z.string().min(1, 'Required'),
    hours: z.string(),
    minutes: z.string(),
    // Optional — blank derives from the employee's monthly salary ÷ 168 (ADR-049).
    hourly_rate: z.string(),
    amount: z.string(),
    tax_rate: z.string(),
    currency: z.string().min(1).max(3),
    // Not shown: the prefilled salary's FX and the currency it belongs to (C3).
    fx_rate_to_base: z.string(),
    fx_currency: z.string(),
    due_date: z.string(),
    notes: z.string(),
  })
  .refine((v) => (Number(v.hours) || 0) + (Number(v.minutes) || 0) / 60 > 0, {
    message: 'Enter time worked',
    path: ['hours'],
  });

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'employee', 'number', 'service_description', 'period_start', 'period_end', 'hours', 'minutes',
  'hourly_rate', 'amount', 'tax_rate', 'currency', 'due_date', 'notes',
] as const;

interface StaffInvoiceFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (input: StaffInvoiceInput) => Promise<unknown>;
  onCancel: () => void;
}

const normCurrency = (code: string) => code.trim().toUpperCase();

interface SalaryAnswer {
  employeeId: number;
  /** The period end the answer is for ('' = today). */
  periodEnd: string;
  isPending: boolean;
  isError: boolean;
  latestSalary: StaffInvoiceSalary['salary'];
  derivedRate: string | null;
}

/** What is wrong with leaving the rate blank — one rule for the note under the
 *  field and for submit. A blank rate is derived by the server from the latest
 *  salary, in the salary's currency, so it is refused (`error`) whenever that
 *  currency isn't the invoice's or isn't known yet; with no salary on file it
 *  saves at 0, which only gets a note. */
function blankRateProblem(
  answer: SalaryAnswer,
  currency: string,
): { note: string | null; error: string | null } | null {
  const refuse = (text: string) => ({ note: text, error: text });
  const warn = (text: string) => ({ note: text, error: null });
  if (answer.employeeId <= 0) return null;
  const { latestSalary } = answer;
  if (answer.isError) return refuse("Couldn't load the salary to derive the rate. Enter the rate.");
  if (answer.isPending) return { note: null, error: 'The salary is still loading. Wait for it, or enter the rate.' };
  if (!latestSalary) {
    return warn(
      answer.periodEnd
        ? 'No salary in effect by the period end. Enter the rate.'
        : 'No salary on file for this employee. Enter the rate.',
    );
  }
  if (!latestSalary.currency) return refuse("This employee's salary is hidden from you. Enter the rate.");
  const invoice = normCurrency(currency);
  if (latestSalary.currency !== invoice) {
    return refuse(`The salary is in ${latestSalary.currency}${invoice ? `, not ${invoice}` : ''}. Enter the rate.`);
  }
  if (!answer.derivedRate) return warn('The latest salary gives no hourly rate. Enter the rate.');
  return null;
}

/** The note under a blank rate (see `blankRateProblem`). Watches the rate
 *  itself so typing a rate doesn't re-render the whole form (the currency is
 *  watched by the form anyway, for the prefill); hidden while the field shows
 *  a submit error with the same text. */
function RateUnavailableNote({
  control,
  answer,
}: {
  control: Control<FormValues>;
  answer: SalaryAnswer;
}) {
  const [rate, currency] = useWatch({ control, name: ['hourly_rate', 'currency'] });
  const { error } = useFormField();
  const note = rate.trim() || error ? null : blankRateProblem(answer, currency)?.note;
  return note ? <FormDescription className="text-xs">{note}</FormDescription> : null;
}

export function StaffInvoiceForm({
  defaultValues,
  submitLabel,
  onSubmit,
  onCancel,
}: StaffInvoiceFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee: '',
      number: '',
      service_description: '',
      period_start: '',
      period_end: '',
      hours: '',
      minutes: '',
      hourly_rate: '',
      amount: '',
      tax_rate: '',
      currency: 'USD',
      fx_rate_to_base: '',
      fx_currency: '',
      due_date: '',
      notes: '',
      ...defaultValues,
    },
  });

  // Derive the hourly rate from the selected employee's latest agreed salary
  // (monthly ÷ 168, ADR-049). The accountant can override the prefilled rate.
  const watchedEmployee = useWatch({ control: form.control, name: 'employee' });
  const employeeId = Number(watchedEmployee) || 0;
  const [periodStart, periodEnd, watchedHours, watchedMinutes] = useWatch({
    control: form.control,
    name: ['period_start', 'period_end', 'hours', 'minutes'],
  });
  const enteredMinutes = Math.round(Number(toDecimalHours(watchedHours, watchedMinutes)) * 60);

  function applyLoggedMinutes(total: number) {
    const { hours, minutes } = splitHours(total / 60);
    form.setValue('hours', hours);
    form.setValue('minutes', minutes);
    // Validate together — the "time worked" rule spans both fields.
    void form.trigger(['hours', 'minutes']);
  }
  // From the finance-owned endpoint — HR salary records are HR-only (DEV13-77).
  // The record in effect at the period end, so a later raise isn't billed for
  // an earlier period (BE ADR-104). Date inputs emit partial years while typing:
  // only a settled, complete date is sent. Until the answer for the period end
  // now in the form arrives, the one shown is treated as still loading.
  const settledPeriodEnd = useDebouncedValue(periodEnd);
  const salaryPeriodEnd = isIsoDay(settledPeriodEnd) ? settledPeriodEnd : '';
  const salaryIsStale = salaryPeriodEnd !== (isIsoDay(periodEnd) ? periodEnd : '');
  const salary = useStaffInvoiceSalary(employeeId, salaryPeriodEnd);
  const latestSalary = salary.data?.salary ?? null;
  // Masked money comes back null together, so a rate always has its currency.
  const derivedRate = latestSalary?.currency ? (latestSalary.hourly_rate ?? null) : null;
  const standardHours = salary.data?.standard_hours ?? STANDARD_MONTHLY_HOURS;
  const salaryAnswer: SalaryAnswer = {
    employeeId,
    periodEnd: salaryPeriodEnd,
    isPending: salary.isPending || salaryIsStale,
    isError: salary.isError,
    latestSalary,
    derivedRate,
  };

  // Prefill the rate from the picked employee's salary. The rate is in the
  // salary's currency (a sent rate skips the server's own currency/FX defaults,
  // so without this a EUR rate saved as USD at FX 1), so one rule compares the
  // invoice currency with the salary's:
  //  - currency untouched (never typed in, or still what we filled) → switch it
  //    to the salary's currency;
  //  - same currency → fill the rate and send the salary's FX;
  //  - another currency the accountant chose → fill no rate; the note says why
  //    and submit refuses a blank rate (`blankRateProblem`; the server too).
  // Only a blank rate or one we filled is (re)filled, so it follows employee and
  // currency changes like `autoFilledNumber` — and is cleared while the next
  // employee's salary loads; a typed or stored rate is left alone with its
  // currency and FX (FX is sent only for the currency it is for).
  const autoFilledRate = useRef('');
  const autoFilledCurrency = useRef('');
  const currencyTyped = useRef(false);
  const defaultCurrency = useRef(normCurrency(form.getValues('currency')));
  const watchedCurrency = useWatch({ control: form.control, name: 'currency' });
  useEffect(() => {
    const rate = form.getValues('hourly_rate');
    if (rate.trim() && rate !== autoFilledRate.current) return;

    const currency = normCurrency(form.getValues('currency'));
    const currencyIsOurs =
      !currencyTyped.current || (autoFilledCurrency.current !== '' && currency === autoFilledCurrency.current);
    // Known even when the salary gives no positive rate (a blank rate then saves
    // at 0 in that currency). While loading, on error or when hidden there is
    // none: what we filled is cleared and the currency goes back to the default.
    const salaryCurrency = latestSalary?.currency ?? '';
    const invoiceCurrency = currencyIsOurs ? salaryCurrency || defaultCurrency.current : currency;
    const fills = Boolean(derivedRate) && salaryCurrency === invoiceCurrency;

    const nextRate = fills && derivedRate ? derivedRate : '';
    if (rate !== nextRate) form.setValue('hourly_rate', nextRate, { shouldValidate: Boolean(nextRate) });
    // A refusal from an earlier submit is about an older answer; the note under
    // the field now says what holds for this one.
    if (!nextRate) form.clearErrors('hourly_rate');
    autoFilledRate.current = nextRate;
    if (currencyIsOurs) {
      if (currency !== invoiceCurrency) form.setValue('currency', invoiceCurrency, { shouldValidate: true });
      autoFilledCurrency.current = salaryCurrency;
      // The field holds our value again, so the next answer (after a load reset
      // it to the default) may switch it; typing makes it the accountant's.
      currencyTyped.current = false;
    }
    form.setValue('fx_rate_to_base', fills ? (latestSalary?.fx_rate_to_base ?? '') : '');
    form.setValue('fx_currency', fills ? salaryCurrency : '');
    // Re-runs per employee answer and per currency edit; the rate and currency
    // themselves are read (and a typed rate kept) above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salary.data, salary.isPending, salary.isError, watchedCurrency]);

  // Suggest the next invoice number for the picked employee — computed by the
  // server (INV-<code>-<year>-NNN, per-employee/year). Only on create.
  const isEdit = Boolean(defaultValues?.number);
  const { data: suggestedNumber } = useNextStaffInvoiceNumber(employeeId, !isEdit);

  // Fill (and re-fill on employee change) as long as the accountant hasn't typed
  // their own number — `autoFilledNumber` tracks what we last set.
  const autoFilledNumber = useRef('');
  useEffect(() => {
    if (isEdit || !suggestedNumber) return;
    const current = form.getValues('number');
    if (!current.trim() || current === autoFilledNumber.current) {
      form.setValue('number', suggestedNumber, { shouldValidate: true });
      autoFilledNumber.current = suggestedNumber;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedNumber]);

  async function handleSubmit(values: FormValues) {
    const blank = values.hourly_rate.trim() ? null : blankRateProblem(salaryAnswer, values.currency);
    if (blank?.error) {
      form.setError('hourly_rate', { message: blank.error });
      return;
    }
    // The rate filled from another period end's salary isn't this period's.
    if (salaryIsStale && derivedRate && values.hourly_rate === derivedRate) {
      form.setError('hourly_rate', { message: 'The salary is still loading. Wait for it, or enter the rate.' });
      return;
    }
    const input: StaffInvoiceInput = {
      employee: Number(values.employee),
      number: values.number,
      period_start: values.period_start,
      period_end: values.period_end,
      hours_worked: toDecimalHours(values.hours, values.minutes),
      currency: normCurrency(values.currency),
      due_date: values.due_date || null,
      notes: values.notes,
    };
    // The salary's FX goes with its currency (C3) while that currency is still
    // the one we filled; a currency the accountant chose keeps the server default.
    if (values.fx_rate_to_base && input.currency === values.fx_currency) {
      input.fx_rate_to_base = values.fx_rate_to_base;
    }
    // Blank description → omit so the server defaults it (ADR-052).
    if (values.service_description.trim()) input.service_description = values.service_description;
    // Blank rate → omit so the server derives from salary ÷ 168 (BE-ADR-049); only
    // reached when that salary is in the invoice currency (checked above).
    if (values.hourly_rate.trim()) input.hourly_rate = values.hourly_rate;
    if (values.amount.trim()) input.amount = values.amount;
    if (values.tax_rate.trim()) input.tax_rate = values.tax_rate;
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          // The server field is `hours_worked`; surface its error on the hours input.
          const key = field === 'hours_worked' ? 'hours' : FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <FormSection variant="card" title="Invoice" description="Who and which period.">
          <FormField
            control={form.control}
            name="employee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employee</FormLabel>
                <FormControl>
                  <EntitySelect
                    queryKey="staff-invoice-employee-picker"
                    value={field.value}
                    onChange={field.onChange}
                    search={searchStaffInvoiceEmployees}
                    resolveLabel={resolveStaffInvoiceEmployeeLabel}
                    placeholder="Select an employee"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="period_start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Period start</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="period_end"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Period end</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="number"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  Number
                  <InfoHint text="Auto-suggested from the employee’s last invoice (+1). Edit to override." />
                </FormLabel>
                <FormControl>
                  <Input placeholder="INV-HRD-001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="service_description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  Service / description
                  <InfoHint text="The line item printed on the invoice PDF. Blank defaults to “Software Development Services”." />
                </FormLabel>
                <FormControl>
                  <Input placeholder="Software Development Services" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Amount" description="Hours × rate; override the total if needed.">
          <div className="grid items-start gap-6 sm:grid-cols-4">
            <FormField
              control={form.control}
              name="hours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hours</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minutes</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" max="59" step="1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="hourly_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Hourly rate
                    <InfoHint
                      text={
                        derivedRate && latestSalary
                          ? `Derived: ${formatMoney(latestSalary.amount, latestSalary.currency)}/mo ÷ ${standardHours}h = ${derivedRate}`
                          : `Prefilled from the latest monthly salary ÷ ${standardHours}h when it is in the invoice currency.`
                      }
                    />
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.01" {...field} />
                  </FormControl>
                  <RateUnavailableNote control={form.control} answer={salaryAnswer} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={3}
                      placeholder="USD"
                      {...field}
                      onChange={(e) => {
                        currencyTyped.current = true; // the accountant's own choice now
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <StaffInvoiceLoggedHours
            employeeId={employeeId}
            periodStart={periodStart}
            periodEnd={periodEnd}
            enteredMinutes={enteredMinutes}
            onUseLogged={applyLoggedMinutes}
          />
          <div className="grid items-start gap-6 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Total (optional)
                    <InfoHint text="Gross. Leave blank to compute automatically." />
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.01" placeholder="Auto = hours × rate" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tax_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Tax %<InfoHint text="% of the gross the employee owes (included)." />
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min="0" max="100" step="0.01" placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="due_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Due date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea rows={2} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
